import { useEffect, useState } from "react";
import { AlertTriangle, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LoadingState } from "@/components/fsl/States";
import { api, apiError } from "@/lib/api";

const MODE_HINT = {
  knockout: "Incroci automatici dalle classifiche (1ªA–2ªB, 1ªB–2ªA…). Le caselle vuote restano segnaposto e si riempiono con «Inserisci le qualificate».",
  placement: "Ogni partita assegna due posti: 1°/2°, 3°/4°, 5°/6°…",
  bracket: "Semifinali incrociate per ogni coppia di posti: vincenti e perdenti si sfidano poi per 1°/2°, 3°/4°, 5°/6°, 7°/8°.",
};

/** Anteprima del 1° turno della fase finale (Motore semplice): squadre proposte dalle classifiche, modificabili prima di generare. */
export function SimpleFinalsPreview({ tid, form, category, onClose, onDone }) {
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post(`/tournaments/${tid}/simple/finals/preview`, { mode: form.mode, teams: Number(form.teams), category }).then(({ data }) => { setP(data); setPairs(data.pairs.map((x) => ({ home: x.home.team_id, away: x.away.team_id }))); }).catch(setError);
  }, [tid, form.mode, form.teams, category]);
  const setSide = (i, side, v) => setPairs((ps) => ps.map((x, j) => (j === i ? { ...x, [side]: v || null } : x)));
  const chosen = pairs.flatMap((x) => [x.home, x.away]).filter(Boolean);
  const dup = chosen.length !== new Set(chosen).size;
  const empty = pairs.flatMap((x) => [x.home, x.away]).filter((v) => !v).length;
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tournaments/${tid}/simple/finals`, { ...form, teams: Number(form.teams), category, pairs });
      toast.success(`Fase finale creata: ${data.count} partite`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-navy-800 border-white/20 text-fsl-white rounded-xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="simple-finals-preview" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Anteprima fase finale</DialogTitle></DialogHeader>
        {error && <p className="text-sm text-fsl-danger" data-testid="simple-finals-preview-error">{apiError(error)}</p>}
        {!p && !error && <LoadingState label="Calcolo accoppiamenti…" />}
        {p && (
          <div className="space-y-4">
            <p className="text-xs text-fsl-slate">{MODE_HINT[p.mode]} Puoi cambiare ogni squadra: lascia vuoto per tenere il segnaposto.</p>
            {p.pending > 0 && <p className="text-xs text-fsl-warning inline-flex items-start gap-1" data-testid="simple-finals-preview-pending"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {p.pending} {p.pending === 1 ? "gara dei gironi non è ancora ufficiale" : "gare dei gironi non sono ancora ufficiali"}: le squadre proposte seguono la classifica attuale e potrebbero cambiare.</p>}
            <div className="space-y-2" data-testid="simple-finals-preview-pairs">
              {p.pairs.map((pair, i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-ink-950/50 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-fsl-gold mb-1" data-testid={`simple-finals-pair-label-${i}`}>{pair.label}</div>
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                    {["home", "away"].map((side, k) => (
                      <label key={side} className={`block ${k === 1 ? "order-3" : ""}`}>
                        <span className="block text-[10px] text-fsl-slate mb-0.5">{pair[side].label}</span>
                        <select className="fsl-input h-10 text-sm" value={pairs[i]?.[side] || ""} onChange={(e) => setSide(i, side, e.target.value)} data-testid={`simple-finals-pair-${i}-${side}`}>
                          <option value="">— segnaposto «{pair[side].label}» —</option>
                          {p.teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.group ? ` (${t.group})` : ""}</option>)}
                        </select>
                      </label>
                    ))}
                    <span className="order-2 text-xs text-fsl-slate font-bold px-1 self-end pb-3">vs</span>
                  </div>
                </div>
              ))}
            </div>
            {dup && <p className="text-xs text-fsl-danger" data-testid="simple-finals-preview-dup">Una squadra compare due volte.</p>}
            {empty > 0 && !dup && <p className="text-xs text-fsl-slate">{empty} {empty === 1 ? "casella resterà segnaposto" : "caselle resteranno segnaposto"}: le compili dopo con «Inserisci le qualificate» o «Modifica».</p>}
          </div>
        )}
        <DialogFooter>
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="simple-finals-preview-cancel">Annulla</button>
          {p && <button type="button" className="btn-primary" disabled={busy || dup} onClick={generate} data-testid="simple-finals-preview-generate"><Wand2 className="h-4 w-4" /> {busy ? "Creazione…" : "Genera con queste squadre"}</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
